import { AccountProfileLinkType } from './accounts';
import { PermissionModel } from './authorization';
import { MediaitemModel } from './mediaitems';
import { BasicGoalSubscriptionModel, Gender } from './shared';
import { SkillGraphHeaderModel } from './skillGraphs';
import { UserModel } from './users';
import { VersionedEntityType } from './versions';

enum GoalCategoryCid {
  AI = 'ai',
  PROGRAMMING = 'programming',
  DATA_ANALYSIS = 'data-analysis',
  SCIENCE = 'science',
  FOREIGN_LANGUAGES = 'foreign-languages',
  BUSINESS = 'business',
  LONGEVITY = 'longevity',
  SPORT_AND_FITNESS = 'sport-and-fitness',
  GENIUS = 'genius',
  ART = 'art',
  FAMILY = 'family'
}

interface GoalChildrenSummaryModel {
  // chatbot
  chatbotEntriesCount: number;
  chatbotEntriesDraftCount: number;
  chatbotEntriesCountPublishThreshold: number;
  chatbotEntityId: number;

  // strategy
  strategyEntityId: string;
  strategyNodesCount: number;
  strategyNodesCountPublishThreshold: number;
  strategyNodesDraftCount: number;
  strategyNodesMarkedToDeleteCount: number;
  strategyPathsDraftCount: number;
  strategyPathsMarkedToDeleteCount: number;

  // tasks
  tasksDraftCount: number;
  taskBlocksDraftCount: number;

  // activation rules
  activationRulesDraftCount: number;
}

interface GoalValidationSummaryModel {
  errorsCount: number;
  warningsCount: number;
}

interface GoalDiffSummaryModel {
  additionsCount: number;
  deletionsCount: number;
}

interface GoalGetFiltersModel {
  subscribed?: boolean;
  liked?: boolean;
  completed?: boolean;
  created?: boolean;
}

interface LikeModel {
  isActive?: boolean;
}

enum GoalCardLinkTarget {
  GOAL = 'GOAL',
  SUBSCRIPTION = 'SUBSCRIPTION'
}

enum GoalSellingStatus {
  ON_SALE = 'ON_SALE',
  PRE_SALE = 'PRE_SALE',
  SOLD_OUT = 'SOLD_OUT'
}

interface GoalPriceModel {
  entityId?: string;
  versionType?: VersionedEntityType;
  currency?: string;
  amount?: number;
}

/** Request body for `POST /goals/{goalEntityId}/prices` (backend: GoalPricePatchSchema). */
type GoalPricePatchModel = GoalPriceModel;

interface GoalPriceDetailsModel {
  amountForMentor?: number;
  /** Mentor payout when the sale is affiliator/imprv-driven (niesamodzielna). */
  amountForMentorAssisted?: number;
  amountForImprover?: number;
  fees?: number;
}

enum GoalLandingPageVideoProvider {
  YOUTUBE = 'YOUTUBE',
  VIMEO = 'VIMEO'
}

interface GoalLandingPagePreviewVideoModel {
  url: string;
  provider?: GoalLandingPageVideoProvider | null;
  title?: string;
  subtitle?: string;
  hidden?: boolean | null;
}

interface GoalLandingPageKeywordsSectionModel {
  sectionTitle?: string;
  keywords?: string[];
  hidden?: boolean | null;
}

interface GoalLandingPageModel {
  accentColor?: string;
  hero?: GoalLandingPageHeroModel;
  overview?: GoalLandingPageOverviewModel;
  mentorSection?: GoalLandingPageMentorSectionModel;
  modulesSection?: GoalLandingPageModulesSectionModel;
  mentors?: GoalLandingPageMentorModel[];
  testimonials?: GoalLandingPageTestimonialModel[];
  faqs?: GoalLandingPageFAQModel[];
  modules?: GoalLandingPageModuleModel[];
  socialProof?: GoalLandingPageSocialProofModel;
  pricing?: GoalLandingPagePricingModel;
  previewVideo?: GoalLandingPagePreviewVideoModel | null;
  keywords?: GoalLandingPageKeywordsSectionModel;
  recommendedGoals?: {
    similarEntityIds?: string[];
    nextEntityIds?: string[];
    prerequisiteEntityIds?: string[];
  };
}

interface GoalPatchModel {
  title?: string;
  shortDescription?: string;
  description?: string;
  price?: GoalPriceModel;
  thumbnailMediaitemId?: number;
  landingPage?: GoalLandingPageModel;
  categoryCids?: GoalCategoryCid[];
}

enum GoalVisibilityStatus {
  LISTED = 'LISTED',
  WIP = 'WIP'
}

enum GoalPrivacy {
  PRIVATE = 'PRIVATE',
  PUBLIC = 'PUBLIC'
}

// ordered as a scale of openness - the dropdown lists the modes in this order
enum GoalVisibilityVectorAccess {
  NOBODY = 'NOBODY',
  SELECTED = 'SELECTED',
  ANYONE = 'ANYONE'
}

interface GoalVisibilityVectorModel {
  access: GoalVisibilityVectorAccess;
  whitelistEmails: string[];
  whitelistDomains: string[];
}

enum GoalType {
  SINGLE = 'SINGLE',
  COMBO = 'COMBO'
}

interface GoalLandingPageTestimonialModel {
  quote: string;
  author: string;
  role: string;
  avatarUrl: string;
}

interface GoalLandingPageFAQModel {
  question: string;
  answer: string;
}

interface GoalLandingPageModuleModel {
  title: string;
  description: string;
}

interface GoalLandingPageNumericalProofModel {
  value: number;
  label: string;
}

enum GoalLandingPageSocialProofFeatureIcon {
  CHECK = 'check',
  STAR = 'star',
  HEART = 'heart',
  PERSON = 'person',
  CUP = 'cup',
  ROCKET = 'rocket',
  SPARKLES = 'sparkles',
  TRENDING = 'trending'
}

interface GoalLandingPageSocialProofFeatureModel {
  text: string;
  icon?: GoalLandingPageSocialProofFeatureIcon;
}

interface GoalLandingPageSocialProofModel {
  title?: string;
  subtitle?: string;
  statistics?: GoalLandingPageNumericalProofModel[];
  features?: GoalLandingPageSocialProofFeatureModel[];
}

interface GoalLandingPagePricingRegularPriceModel {
  amount?: number;
  currency?: string;
}

interface GoalLandingPagePricingModel {
  title?: string;
  description?: string;
  benefits?: string[];
  includedTitle?: string;
  included?: string[];
  regularPrice?: GoalLandingPagePricingRegularPriceModel;
}

interface GoalLandingPageMentorSocialLinkModel {
  type: AccountProfileLinkType;
  url: string;
}

interface GoalLandingPageMentorSectionModel {
  sectionTitle?: string;
  sectionLeader?: string;
  introduction?: string;
}

interface GoalLandingPageModulesSectionModel {
  sectionTitle?: string;
  sectionLeader?: string;
}

interface GoalLandingPageMentorModel {
  gender: Gender;
  socialLinks: GoalLandingPageMentorSocialLinkModel[];
  name: string;
  title: string;
  bio: string;
  avatarUrl: string;
}

interface GoalLandingPageHeroModel {
  imageUrl: string;
  title?: string;
  shortDescription?: string;
  ctaLabel?: string;
  ctaSecondaryLabel?: string;
}

interface GoalLandingPageOverviewBenefitModel {
  title: string;
  items: string[];
}

interface GoalLandingPageOverviewModel {
  title: string;
  subtitle: string;
  description: string;
  benefit: GoalLandingPageOverviewBenefitModel;
}

enum GoalEnrollReason {
  ACQUIRED_AT_GOTOIT = 'ACQUIRED_AT_GOTOIT',
  ACQUIRED_AT_IMPRV = 'ACQUIRED_AT_IMPRV',
  FREE = 'FREE'
}

interface GoalEnrollSpecModel {
  reason?: GoalEnrollReason | null;
  canEnroll: boolean;
}

interface GoalModel {
  versionType?: VersionedEntityType;
  availableVersionTypes?: VersionedEntityType[];
  entityId?: string;
  entityCreatedAt?: Date | null;
  title?: string;
  shortDescription?: string;
  description?: string;
  logoSvg?: string;
  liked?: LikeModel;
  subscription?: BasicGoalSubscriptionModel;
  user?: UserModel;
  createdAt?: string;
  childrenSummary?: GoalChildrenSummaryModel | null;
  __permissions?: PermissionModel[];
  visibilityStatus?: GoalVisibilityStatus;
  privacy: GoalPrivacy;
  releaseDate?: Date | null;
  type?: GoalType;
  thumbnailMediaitemId?: number;
  isChildGoal?: boolean;
  categoryCids?: GoalCategoryCid[];
  recommendationScore?: number | null;
  activeSubscriptionsCount?: number;
  validationSummary?: GoalValidationSummaryModel | null;
  diffSummary?: GoalDiffSummaryModel | null;
  changedFields?: string[] | null;

  // Selling
  currentPrice?: GoalPriceModel;
  externalSaleUrl?: string;
  sellingStatus?: GoalSellingStatus;
  enrollSpec?: GoalEnrollSpecModel;

  // This is filled by the fetch by id, not by the fetch all
  childGoalEntityIds?: string[];
  thumbnailMediaitem?: MediaitemModel;

  // Landing page specific fields
  landingPageYaml?: string | null;
  landingPage?: GoalLandingPageModel | null;

  // Skill graph attachment (exposed by the API as a header only)
  skillGraph?: SkillGraphHeaderModel | null;
}

interface ComboGoalGraphModel {
  comboGoal: GoalModel;
  childGoals: GoalModel[];
}

const MAX_PACKAGE_DISCOUNT_PERCENT = 50;

const hasValidPackageDiscount = (discountPercentage?: number): boolean =>
  typeof discountPercentage === 'number' &&
  discountPercentage > 0 &&
  discountPercentage <= MAX_PACKAGE_DISCOUNT_PERCENT;

const isAcquiredViaEnroll = (goal: GoalModel): boolean =>
  goal.enrollSpec?.canEnroll === true && goal.enrollSpec?.reason !== GoalEnrollReason.FREE;

const isGoalOwned = (goal: GoalModel, goalSubscription?: unknown): boolean =>
  !!goalSubscription || !!goal.subscription || isAcquiredViaEnroll(goal);

// Mentor per-goal earnings (all money fields are minor units / cents).
export interface GoalSaleModel {
  orderId: string;
  buyerEmail: string | null;
  grossAmount: number;
  netAmount: number;
  currency: string;
  createdAt: string;
}

export interface GoalMonthlyEarningsModel {
  month: string; // "YYYY-MM"
  net: number;
  gross: number;
}

export interface GoalEarningsSummaryModel {
  netTotal: number;
  grossTotal: number;
  unitsSold: number;
}

export interface GoalEarningsModel {
  goalEntityId: string;
  currency: string | null;
  summary: GoalEarningsSummaryModel;
  monthly: GoalMonthlyEarningsModel[];
  sales: GoalSaleModel[];
}

export interface MentorSaleModel {
  orderId: string;
  goalEntityId: string | null;
  goalTitle: string | null;
  buyerEmail: string | null;
  grossAmount: number;
  netAmount: number;
  currency: string;
  createdAt: string;
}

export interface MentorEarningsModel {
  currency: string | null;
  summary: GoalEarningsSummaryModel;
  monthly: GoalMonthlyEarningsModel[];
  sales: MentorSaleModel[];
}

export {
  GoalCardLinkTarget,
  GoalCategoryCid,
  GoalEnrollReason,
  GoalLandingPageSocialProofFeatureIcon,
  GoalLandingPageVideoProvider,
  GoalPrivacy,
  GoalSellingStatus,
  GoalType,
  GoalVisibilityStatus,
  GoalVisibilityVectorAccess,
  hasValidPackageDiscount,
  isAcquiredViaEnroll,
  isGoalOwned,
  MAX_PACKAGE_DISCOUNT_PERCENT
};
export type {
  ComboGoalGraphModel,
  GoalChildrenSummaryModel,
  GoalDiffSummaryModel,
  GoalEnrollSpecModel,
  GoalGetFiltersModel,
  GoalLandingPageFAQModel,
  GoalLandingPageHeroModel,
  GoalLandingPageKeywordsSectionModel,
  GoalLandingPageMentorModel,
  GoalLandingPageMentorSectionModel,
  GoalLandingPageMentorSocialLinkModel,
  GoalLandingPageModel,
  GoalLandingPageModuleModel,
  GoalLandingPageModulesSectionModel,
  GoalLandingPageNumericalProofModel,
  GoalLandingPageOverviewBenefitModel,
  GoalLandingPageOverviewModel,
  GoalLandingPagePreviewVideoModel,
  GoalLandingPagePricingModel,
  GoalLandingPageSocialProofFeatureModel,
  GoalLandingPageSocialProofModel,
  GoalLandingPageTestimonialModel,
  GoalModel,
  GoalPatchModel,
  GoalPriceDetailsModel,
  GoalPriceModel,
  GoalPricePatchModel,
  GoalValidationSummaryModel,
  GoalVisibilityVectorModel,
  LikeModel
};
